import {
  Configuration,
  ConfigurationItem,
  ConfigurationAnswer
} from '@/models/configurator/configuration';

import {
  ConfigurationService,
  IConfigurationFilterParam,
  IConfigurationPaginatedListData
} from '@/services/configurator/configuration';
import { ActionTree, GetterTree, MutationTree } from 'vuex';

export type IFetchConfigurationActionPayload = IConfigurationFilterParam;

export type ConfigurationState = typeof state;

const configurationService = new ConfigurationService();

export const state = {
  configurations: [] as Configuration[],
  configurationItems: [] as ConfigurationItem[],
  answers: [] as ConfigurationAnswer[],
  count: 0,
  lastPromise: null as null | Promise<any>
};

export const mutations: MutationTree<ConfigurationState> = {
  SET_COUNT(state, payload: number): void {
    state.count = payload;
  },

  INCREMENT_COUNT(state): void {
    state.count += 1;
  },

  DECREMENT_COUNT(state): void {
    state.count -= 1;
  },

  SET_LAST_PROMISE(state, payload: Promise<any>): void {
    state.lastPromise = payload;
  },

  ADD_CONFIGURATION(state, payload: Configuration) {
    state.configurations.push(payload);
  },

  SET_CONFIGURATIONS(state, payload: Configuration[]) {
    state.configurations = payload;
  },

  UPDATE_CONFIGURATION(state, payload: Configuration) {
    const index = state.configurations.findIndex(
      configuration => configuration.id === payload.id
    );
    state.configurations.splice(index, 1, payload);
  },

  REMOVE_CONFIGURATION(state, payload: number) {
    const index = state.configurations.findIndex(
      configuration => configuration.id === payload
    );
    state.configurations.splice(index, 1);
  },

  SET_CONFIGURATION_ITEMS(state, payload: ConfigurationItem[]) {
    state.configurationItems = payload;
  },

  ADD_CONFIGURATION_ITEM(state, payload: ConfigurationItem) {
    state.configurationItems.push(payload);
  },

  UPDATE_CONFIGURATION_ITEM(state, payload: ConfigurationItem) {
    const index = state.configurationItems.findIndex(
      configuration => configuration.id === payload.id
    );
    state.configurationItems.splice(index, 1, payload);
  },

  REMOVE_CONFIGURATION_ITEM(state, payload: number) {
    const index = state.configurationItems.findIndex(
      configuration => configuration.id === payload
    );
    state.configurationItems.splice(index, 1);
  },

  SET_ANSWERS(state, payload: ConfigurationAnswer[]) {
    state.answers = payload;
  }
};

export const actions: ActionTree<ConfigurationState, ConfigurationState> = {
  createConfiguration: async ({ commit, state }) =>
    await new Promise((resolve, reject) => {
      configurationService
        .createConfiguration()
        .then(configuration => {
          // TODO: LFF-2022-12-20 - The mutations here don't make sense
          const index = state.configurations.findIndex(
            s => s.id === configuration.id
          );
          if (index === -1) {
            commit('ADD_CONFIGURATION', configuration);
            commit('INCREMENT_COUNT');
          } else {
            commit('UPDATE_CONFIGURATION', configuration);
          }
          resolve(configuration);
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchConfigurations(
    { state, commit },
    payload: IFetchConfigurationActionPayload
  ) {
    const promise = configurationService.fetchConfigurations(payload);
    commit('SET_LAST_PROMISE', promise);

    return promise.then((configurations: IConfigurationPaginatedListData) => {
      if (promise == state.lastPromise) {
        commit('SET_CONFIGURATIONS', configurations.results);
        commit('SET_COUNT', configurations.count);
        commit('SET_LAST_PROMISE', null);
      }
    });
  },

  fetchConfiguration: ({ commit, state }, id: number) =>
    new Promise((resolve, reject) => {
      configurationService
        .fetchConfiguration(id)
        .then(configuration => {
          const index = state.configurations.findIndex(
            s => s.id === configuration.id
          );
          if (index === -1) {
            commit('ADD_CONFIGURATION', configuration);
            commit('INCREMENT_COUNT');
          } else {
            commit('UPDATE_CONFIGURATION', configuration);
          }
          resolve(configuration);
        })
        .catch(err => {
          reject(err);
        });
    }),

  updateConfiguration: ({ commit, state }, payload: Configuration) =>
    new Promise((resolve, reject) => {
      configurationService
        .updateConfiguration(payload)
        .then(configuration => {
          const index = state.configurations.findIndex(
            s => s.id === configuration.id
          );
          if (index === -1) {
            commit('ADD_CONFIGURATION', configuration);
            commit('INCREMENT_COUNT');
          } else {
            commit('UPDATE_CONFIGURATION', configuration);
          }
          resolve(configuration);
        })
        .catch(err => {
          reject(err);
        });
    }),

  deleteConfiguration: ({ commit }, id: number) =>
    configurationService.deleteConfiguration(id).then(() => {
      commit('REMOVE_CONFIGURATION', id);
      commit('DECREMENT_COUNT');
    }),

  // Extra configuration functions
  fetchConfigurationItem: (
    { commit },
    { configurationId, id }: { configurationId: number; id: number }
  ) =>
    new Promise((resolve, reject) => {
      configurationService
        .fetchConfigurationItem(configurationId, id)
        .then(items => {
          commit('ADD_CONFIGURATION_ITEM', items);
          resolve(items);
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchConfigurationItems: ({ commit }, id: number) =>
    new Promise((resolve, reject) => {
      configurationService
        .fetchConfigurationItems(id)
        .then(items => {
          commit('SET_CONFIGURATION_ITEMS', items);
          resolve(items);
        })
        .catch(err => {
          reject(err);
        });
    }),

  createConfigurationItem: ({ commit }, payload: ConfigurationItem) =>
    configurationService.createConfigurationItem(payload).then(item => {
      commit('ADD_CONFIGURATION_ITEM', item);
      return item;
    }),

  updateConfigurationItem: ({ state, commit }, payload: ConfigurationItem) =>
    configurationService.updateConfigurationItem(payload).then(item => {
      const index = state.configurationItems.findIndex(s => s.id === item.id);

      if (index === -1) {
        commit('ADD_CONFIGURATION_ITEM', item);
      } else {
        commit('UPDATE_CONFIGURATION_ITEM', item);
      }
      return item;
    }),

  deleteConfigurationItem: ({ state, commit }, payload: ConfigurationItem) =>
    configurationService.deleteConfigurationItem(payload).then(item => {
      commit('REMOVE_CONFIGURATION_ITEM', item);
    }),

  fetchConfigurationAnswers: ({ commit }, id: number) =>
    new Promise((resolve, reject) => {
      configurationService
        .fetchConfigurationAnswers(id)
        .then(answers => {
          commit('SET_ANSWERS', answers);
          resolve(answers);
        })
        .catch(err => {
          reject(err);
        });
    }),

  uploadAfterMarketPartsItems(
    _,
    payload: { configurationId: number; file: File; snapshot?: number }
  ): Promise<void> {
    return configurationService.uploadAfterMarketPartsItems(
      payload.configurationId,
      payload.file,
      payload.snapshot
    );
  }
};
export const getters: GetterTree<ConfigurationState, ConfigurationState> = {
  getConfigurationById: state => (id: number) => {
    return state.configurations.find(
      (configuration: any) => configuration.id === id
    );
  },
  getConfigurationCount(state): number {
    return state.count;
  },
  getConfigurations(state): Configuration[] {
    return state.configurations;
  }
};
